/**
 * Notification bell and panel — Specification §13.
 *
 * The badge polls a dedicated count endpoint rather than the list, because it
 * is checked far more often than the panel is opened and the count hits a
 * partial index instead of reading rows.
 *
 * Opening the panel does not mark anything read. Marking-on-open is how people
 * lose track of what they have actually seen — the "mark all read" action is
 * deliberate and explicit.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api';
import { Button, formatAge } from './ui';

interface NotificationItem {
  readonly id: string;
  readonly type: string;
  readonly title: string;
  readonly body: string;
  readonly confirmationId: string | null;
  readonly readAt: string | null;
  readonly createdAt: string;
}

/** Types that mean something needs acting on, rather than merely happened. */
const URGENT = new Set(['SLA_OVERDUE', 'SLA_APPROACHING']);

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const badge = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ unread: number }>('/api/v1/notifications/unread-count'),
    refetchInterval: 60_000,
  });

  const list = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.get<{ items: NotificationItem[]; unread: number }>('/api/v1/notifications'),
    enabled: open,
  });

  const markAll = useMutation({
    mutationFn: () => api.post('/api/v1/notifications/read-all'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Close on outside click and on Escape — a panel that traps the user is
  // worse than no panel.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (panelRef.current !== null && !panelRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const unread = badge.data?.unread ?? 0;

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        className="interactive relative flex items-center gap-1.5 border px-2.5 py-1.5 text-sm font-medium"
        style={{
          borderRadius: 'var(--radius-sm)',
          background: open ? 'var(--color-accent-subtle)' : 'var(--color-surface)',
          borderColor: open ? 'var(--color-accent)' : 'var(--color-rule-strong)',
          color: open ? 'var(--color-accent)' : 'var(--color-ink-muted)',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
          <path d="M8 1.5a4 4 0 0 0-4 4v2.2L2.7 10a.6.6 0 0 0 .5.9h9.6a.6.6 0 0 0 .5-.9L12 7.7V5.5a4 4 0 0 0-4-4Zm0 12.5a1.8 1.8 0 0 0 1.8-1.5H6.2A1.8 1.8 0 0 0 8 14Z" />
        </svg>
        Notifications
        {unread > 0 && (
          <span
            className="tabular animate-scale ml-0.5 min-w-4 px-1 text-xs font-semibold"
            style={{
              borderRadius: '999px',
              background: 'var(--color-brand-red)',
              color: '#ffffff',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="animate-rise absolute right-0 z-20 mt-1 w-[min(24rem,calc(100vw-2rem))] overflow-hidden border"
          style={{
            background: 'var(--color-surface)',
            borderColor: 'var(--color-rule-strong)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          <div
            className="flex items-center justify-between border-b px-3 py-2"
            style={{ borderColor: 'var(--color-rule)' }}
          >
            <span className="text-sm font-semibold">Notifications</span>
            {unread > 0 && (
              <Button variant="ghost" loading={markAll.isPending} onClick={() => markAll.mutate()}>
                Mark all read
              </Button>
            )}
          </div>

          <div className="max-h-[26rem] overflow-y-auto">
            {list.isPending && (
              <div className="px-3 py-6 text-center text-sm" style={{ color: 'var(--color-ink-subtle)' }}>
                Loading…
              </div>
            )}

            {list.isSuccess && list.data.items.length === 0 && (
              <div className="px-3 py-8 text-center">
                <p className="text-sm font-medium" style={{ color: 'var(--color-ink-muted)' }}>
                  Nothing to catch up on
                </p>
                <p className="mt-1 text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                  You will be told when a confirmation needs you.
                </p>
              </div>
            )}

            {list.isSuccess && list.data.items.length > 0 && (
              <ul className="divide-y" style={{ borderColor: 'var(--color-rule)' }}>
                {list.data.items.map((item) => {
                  const unreadItem = item.readAt === null;
                  const urgent = URGENT.has(item.type);
                  const content = (
                    <div
                      className="px-3 py-2.5"
                      style={{
                        background: unreadItem ? 'var(--color-accent-subtle)' : undefined,
                        borderLeft: urgent ? '2px solid var(--color-danger)' : '2px solid transparent',
                      }}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span
                          className="text-sm font-medium"
                          style={{ color: urgent ? 'var(--color-danger)' : 'var(--color-ink)' }}
                        >
                          {item.title}
                        </span>
                        <span
                          className="tabular shrink-0 text-xs"
                          style={{ color: 'var(--color-ink-subtle)' }}
                        >
                          {formatAge(item.createdAt)}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs" style={{ color: 'var(--color-ink-muted)' }}>
                        {item.body}
                      </div>
                    </div>
                  );

                  return (
                    <li key={item.id}>
                      {item.confirmationId === null ? (
                        content
                      ) : (
                        <Link
                          to={`/confirmations/${item.confirmationId}`}
                          onClick={() => setOpen(false)}
                          className="row-hover block"
                        >
                          {content}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
